import { FormElement, Field } from '@progress/kendo-react-form';
import { Label } from '@progress/kendo-react-labels';
import { Checkbox, TextArea } from '@progress/kendo-react-inputs';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { SchedulerFormEditorProps } from '@progress/kendo-react-scheduler';
import { ActivityEditor, EmployerEditor, } from './Editor';
import { useEffect, useState, useRef } from 'react';
import { AppConstants } from '../Shared/Constants/AppConstant';
import DiaryService from '../Services/DiaryService';
import SweetAlertService from '../../../services/SweetAlertService';
import moment from 'moment';

export const CustomFormEditor = (props: SchedulerFormEditorProps, activityList: any, employerList: any, showRecurrenceEditor: any, dairyActivityList: any, dataItem: any, onFilteredActivityListChange?: (list: any) => void) => {
    const [isTimePicker, setIsTimePicker] = useState(true);
    const [isAllDayCheckbox, setIsAllDayCheckbox] = useState(false);
    const [isOnDutyCheckbox, setIsOnDutyCheckbox] = useState(false);
    const [isDutyOnCallCategorySelected, setIsDutyOnCallCategorySelected] = useState(false);
    const [isDutyOnCallCategorySelectedWhenDCCORICCActivityExists, setIsDutyOnCallCategorySelectedWhenDCCORICCActivityExists] = useState(false);
    const [filteredActivityList, setFilteredActivityList] = useState(activityList);

    // Separate date/time state
    const [startDate, setStartDate] = useState(new Date());
    const [startTime, setStartTime] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [endTime, setEndTime] = useState(new Date());

    // Refs to access TimePicker internals
    const startTimePickerRef = useRef<any>(null);
    const endTimePickerRef = useRef<any>(null);

    // iOS fix: Backspace handler for recurrence inputs
    const backspaceListeners = useRef<Array<{ input: HTMLInputElement, handler: (e: KeyboardEvent) => void }>>([]);

    // Device detection for conditional adaptive mode
    const [isMobileDevice, setIsMobileDevice] = useState(false);

    // Set initial filtered activities based on current employer's SubRole
    useEffect(() => {
        const currentEmployerId = props.valueGetter('memberEmployerId');
        if (currentEmployerId) {
            const currentEmployer: any = employerList.find((emp: any) => emp?.id === currentEmployerId);
            if (currentEmployer && currentEmployer?.subRoleId) {
                DiaryService.getAllWorkActivitiesForDiary(currentEmployer?.subRoleId).then((response) => {
                    if (response?.data) {
                        let otherActivity = {
                            id: -1,
                            name: AppConstants.otherActivityName,
                            jobSubCategoryId: AppConstants.otherActivitySubCatgoryId,
                        }
                        response?.data.push(otherActivity);
                        setFilteredActivityList(response?.data);
                        onFilteredActivityListChange?.(response?.data);
                    }
                }).catch(() => {
                    SweetAlertService.error("Error", "Failed to load activities for this role. Please try again.");
                });
            }
        }
    }, []);

    //var currentDate = dataItem.start;
    //const startRecurrence = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 0, 0, 0));
    const [recurrenceStartDate, setRecurrenceStartDate] = useState(dataItem.start);

    const intersects = (
        startTime1: Date,
        endTime1: Date,
        startTime2: Date,
        endTime2: Date
    ) =>
        (startTime1 < startTime2 && endTime1 > endTime2) ||
        (startTime2 <= startTime1 && startTime1 < endTime2) ||
        (startTime2 < endTime1 && endTime1 <= endTime2);

    //To show time-picker on change of is all day event
    const showOrHideTimePicker = (value: any) => {
        if (value) {
            setIsTimePicker(false);
        } else {
            setIsTimePicker(true);
        }
    }

    // Quick select button handlers
    const handleNowClick = () => {
        const now = new Date();
        const end = new Date(now.getTime() + 30 * 60000); // Add 30 minutes
        setStartDate(now);
        setStartTime(now);
        props.onChange("start", { value: now });
        setEndDate(end);
        setEndTime(end);
        props.onChange("end", { value: end });
        setRecurrenceStartDate(now);
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    const handleAdd15MinClick = () => {
        const currentEnd = props.valueGetter('end');
        const newEnd = new Date(currentEnd.getTime() + 15 * 60000); // Add 15 minutes
        setEndDate(newEnd);
        setEndTime(newEnd);
        props.onChange("end", { value: newEnd });
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    const handle1HourBlockClick = () => {
        const currentStart = props.valueGetter('start');
        const newEnd = new Date(currentStart.getTime() + 60 * 60000); // Add 1 hour
        setEndDate(newEnd);
        setEndTime(newEnd);
        props.onChange("end", { value: newEnd });
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    const handle30MinBlockClick = () => {
        const currentStart = props.valueGetter('start');
        const newEnd = new Date(currentStart.getTime() + 30 * 60000); // Add 30 minutes
        setEndDate(newEnd);
        setEndTime(newEnd);
        props.onChange("end", { value: newEnd });
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    //On activity change
    const onActivityChange = (value: any) => {
        //To get selected activity details
        let selectedActivity = filteredActivityList.filter((x: any) => x.jobSubCategoryId == value)[0];
        //To check isAllDay checkbox
        let isShowAllDay = (selectedActivity?.jobCategoryShortName === AppConstants.dutyDaySubCategoryName || selectedActivity?.jobCategoryShortName === AppConstants.leaveSubCategoryName);
        setIsAllDayCheckbox(isShowAllDay);
        showOrHideTimePicker(isShowAllDay);
        props.onChange("isAllDay", { value: isShowAllDay });
        //To check isOnDuty checkbox
        if (selectedActivity?.jobCategoryShortName === AppConstants.dccActivtyShortName || selectedActivity?.jobCategoryShortName === AppConstants.iccActivtyShortName) {
            setIsOnDutyCheckbox(true);
            //Checking any dutyday activity exits or not
            let isDutyDayExist = (dairyActivityList.filter((x: any) => x.jobSubCategoryName == AppConstants.dutyDaySubCategoryName && (x.id != props.valueGetter('id'))
                && intersects(props.valueGetter('start'), props.valueGetter('end'), x.start, x.end))).length > 0;
            props.onChange("isOnDuty", { value: isDutyDayExist });
        } else {
            setIsOnDutyCheckbox(false);
            props.onChange("isOnDuty", { value: false });
        }
        if (selectedActivity.jobSubCategoryId == AppConstants.dutyOnCallSubCategoryIdForactivitydropdown) {
            setIsDutyOnCallCategorySelected(true)
        } else {
            setIsDutyOnCallCategorySelected(false)
        }
        //Calling the method to check any dcc/icc activity exists
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    const isValidDailyRecurrence = (rule: string) => {
        const pattern = "FREQ=DAILY;WKST=MO";
        return pattern == rule;
    }

    const isValidWeeklyRecurrence = (rule: string, currentDay: any) => {
        const pattern = `FREQ=WEEKLY;BYDAY=${currentDay};WKST=MO`;
        return pattern == rule;
    }

    function isValidMonthlyRecurrence(rule: string, currentDate: any): boolean {
        const pattern = `FREQ=MONTHLY;BYMONTHDAY=${currentDate};WKST=MO`;
        return pattern == rule;
    }

    function isValidYearlyRecurrence(rule: string, currentMonth: any, currentDate: any): boolean {
        const pattern = `FREQ=YEARLY;BYMONTH=${currentMonth};BYMONTHDAY=${currentDate};WKST=MO`;
        return pattern == rule;
    }

    // Initialize separate date/time from props - only when activity changes, not on every update
    useEffect(() => {
        const start = props.valueGetter('start');
        const end = props.valueGetter('end');
        if (start) {
            setStartDate(start);
            setStartTime(start);
        }
        if (end) {
            setEndDate(end);
            setEndTime(end);
        }
    }, [props.valueGetter('id')]); // Only re-initialize when editing a different activity

    //While updating activty checking for isOnDuty and isAllDay
    useEffect(() => {
        if (props.valueGetter('id')) {
            let selectedActivity = filteredActivityList.filter((x: any) => x.jobSubCategoryId == props.valueGetter('jobSubCategoryId'))[0];
            let isShowAllDay = (selectedActivity?.jobCategoryShortName === AppConstants.dutyDaySubCategoryName || selectedActivity?.jobCategoryShortName === AppConstants.leaveSubCategoryName);
            setIsAllDayCheckbox(isShowAllDay);
            let isAllDay = props.valueGetter('isAllDay');
            showOrHideTimePicker(isAllDay);
            props.onChange("isAllDay", { value: isAllDay });

            let isShowDutyOnCall = (selectedActivity?.jobCategoryShortName === AppConstants.dccActivtyShortName || selectedActivity?.jobCategoryShortName === AppConstants.iccActivtyShortName);
            setIsOnDutyCheckbox(isShowDutyOnCall);
            props.onChange("isOnDuty", { value: props.valueGetter('isOnDuty') });
            setIsDutyOnCallCategorySelected(props.valueGetter('jobSubCategoryId') == AppConstants.dutyOnCallSubCategoryIdForactivitydropdown);
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }

        const endRule = document.querySelectorAll('input[type="tel"][id="k-endrule-after"]')[0];
        if (endRule) {
            endRule.setAttribute('maxLength', '2');
        }

        const intervalEditor = document.querySelectorAll('input[type="tel"][id="interval-editor"]')[0];
        if (intervalEditor) {
            intervalEditor.setAttribute('maxLength', '2');
        }

        //TODO: 
        let startTime = props.valueGetter('start');
        //setRecurrenceStartDate(startTime);

        const neverRadioButton = document.querySelector('input[type="radio"][value="never"]');
        if (neverRadioButton && neverRadioButton.parentElement) {
            neverRadioButton.removeAttribute("checked");
            neverRadioButton.parentElement.style.display = 'none';
        }

        const currentDay: string = moment(startTime).format('dd').toUpperCase();
        const currentDate: number = moment(startTime).date();
        const currentMonth: number = moment(startTime).month() + 1;

        const countOccurrenceButton = document.querySelector('input[type="radio"][value="count"]');

        if (countOccurrenceButton) {
            countOccurrenceButton.setAttribute("checked", "true");
            let recurrenceRule = props.valueGetter('recurrenceRule');

            if (recurrenceRule.includes("FREQ=DAILY") && isValidDailyRecurrence(recurrenceRule)) {
                props.onChange("recurrenceRule", { value: "FREQ=DAILY;COUNT=1;WKST=MO" });
            } else if (recurrenceRule.includes("FREQ=WEEKLY") && isValidWeeklyRecurrence(recurrenceRule, currentDay)) {
                let recurrenceRule = `FREQ=WEEKLY;COUNT=1;BYDAY=${currentDay};WKST=MO`;
                props.onChange("recurrenceRule", { value: recurrenceRule });
            } else if (recurrenceRule.includes("FREQ=MONTHLY") && isValidMonthlyRecurrence(recurrenceRule, currentDate)) {
                let recurrenceRule = `FREQ=MONTHLY;COUNT=1;BYMONTHDAY=${currentDate};WKST=MO`;
                props.onChange("recurrenceRule", { value: recurrenceRule });
            } else if (recurrenceRule.includes("FREQ=YEARLY") && isValidYearlyRecurrence(recurrenceRule, currentMonth, currentDate)) {
                let recurrenceRule = `FREQ=YEARLY;COUNT=1;BYMONTH=${currentMonth};BYMONTHDAY=${currentDate};WKST=MO`;
                props.onChange("recurrenceRule", { value: recurrenceRule });
            }
        }
    }, [props.valueGetter('recurrenceRule')]);

    //If user selects duty/on-call activity and checking any dcc/icc activity exists or not,if exists showing the text message info 
    const checkDCCOrICCActivityExistsWhenDutyOncallSelected = () => {
        let currentActivity = props.valueGetter('jobSubCategoryId');
        let currentRecurrenceRule = props.valueGetter('recurrenceRule');
        // For normal activites
        if (currentActivity == AppConstants.dutyOnCallSubCategoryIdForactivitydropdown && !currentRecurrenceRule) {
            let currentActivityStart = props.valueGetter('start');
            let currentActivityEnd = props.valueGetter('end');
            let currentActivityIsAllDay = props.valueGetter('isAllDay');
            let currentId = props.valueGetter('id');
            let currentMemberEmployerId = props.valueGetter('memberEmployerId');
            let selectedActivityStart = currentActivityIsAllDay ? new Date(currentActivityStart.getFullYear(), currentActivityStart.getMonth(), currentActivityStart.getDate(), 0, 0, 0) : currentActivityStart;
            let selectedActivityEnd = currentActivityIsAllDay ? new Date(currentActivityEnd.getFullYear(), currentActivityEnd.getMonth(), currentActivityEnd.getDate(), 23, 59, 59) : currentActivityEnd;
            if (dairyActivityList.filter((x: any) => x.id != currentId
                && x.memberEmployerId == currentMemberEmployerId
                && (x.jobCategoryId == AppConstants.DCC_Category_Id || x.jobCategoryId == AppConstants.ICC_Category_Id))
                .some((x: any) => intersects(selectedActivityStart, selectedActivityEnd, x.start, x.end))) {
                setIsDutyOnCallCategorySelectedWhenDCCORICCActivityExists(true);
            } else {
                setIsDutyOnCallCategorySelectedWhenDCCORICCActivityExists(false);
            }
        } else {
            setIsDutyOnCallCategorySelectedWhenDCCORICCActivityExists(false);
        }
    }

    //On employer change
    const onEmployerChange = (value: any) => {
        if (value) {
            // Get SubRole from selected employer and update activity list
            const selectedEmployer: any = employerList.find((emp: any) => emp?.id === value);
            if (selectedEmployer && selectedEmployer?.subRoleId) {
                DiaryService.getAllWorkActivitiesForDiary(selectedEmployer?.subRoleId).then((response) => {
                    if (response?.data) {
                        //To add other option in the activity list
                        let otherActivity = {
                            id: -1,
                            name: AppConstants.otherActivityName,
                            jobSubCategoryId: AppConstants.otherActivitySubCatgoryId,
                        }
                        response?.data.push(otherActivity);
                        setFilteredActivityList(response?.data);
                        onFilteredActivityListChange?.(response?.data);

                        // Reset activity selection when employer changes
                        props.onChange("jobSubCategoryId", { value: null });
                    }
                }).catch(() => {
                    SweetAlertService.error("Error", "Failed to load activities for selected workplace. Please try again.");
                });
            }
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    }

    // Sync helper function
    const combineDateTime = (date: Date, time: Date) => {
        const combined = new Date(date.getFullYear(), date.getMonth(), date.getDate(),
            time.getHours(), time.getMinutes(),
            time.getSeconds(), time.getMilliseconds());
        // Apply existing logic: preserve 23:59:59 for all-day, zero others
        if (!(combined.getHours() === 23 && combined.getMinutes() === 59)) {
            combined.setSeconds(0, 0);
        }
        return combined;
    };

    //On stat change
    const onStartDateChange = (event: any) => {
        if (event.value) {
            var currentDate = event.value;
            setRecurrenceStartDate(currentDate);
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    }

    // Separate picker handlers
    const handleStartDateChange = (value: Date) => {
        if (value) {
            setStartDate(value);
            const combined = combineDateTime(value, startTime);
            props.onChange("start", { value: combined });
            setRecurrenceStartDate(combined);
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    };

    const handleStartTimeChange = (value: Date | null) => {
        if (value) {
            setStartTime(value);
            const combined = combineDateTime(startDate, value);
            props.onChange("start", { value: combined });
            setRecurrenceStartDate(combined);
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    };

    //On end change
    const onEndDateChange = (event: any) => {
        if (event.value) {
            const endTime = new Date(event?.value);
            // Only reset seconds to 0 for all day activity
            if (!(endTime.getHours() === 23 && endTime.getMinutes() === 59)) {
                endTime.setSeconds(0, 0);
                props.onChange("end", { value: endTime });
            }
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    }

    const handleEndDateChange = (value: Date) => {
        if (value) {
            setEndDate(value);
            const combined = combineDateTime(value, endTime);
            if (!(combined.getHours() === 23 && combined.getMinutes() === 59)) {
                combined.setSeconds(0, 0);
            }
            props.onChange("end", { value: combined });
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    };

    const handleEndTimeChange = (value: Date | null) => {
        if (value) {
            setEndTime(value);
            const combined = combineDateTime(endDate, value);
            if (!(combined.getHours() === 23 && combined.getMinutes() === 59)) {
                combined.setSeconds(0, 0);
            }
            props.onChange("end", { value: combined });
            //Calling the method to check any dcc/icc activity exists
            checkDCCOrICCActivityExistsWhenDutyOncallSelected();
        }
    };

    //On all-day checkbox event change
    const onAllDayCheckBoxChange = (value: any) => {
        showOrHideTimePicker(value);
        //Calling the method to check any dcc/icc activity exists
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    //On reccurence editor change
    const onReccurenceEditorChange = (e: any) => {
        //Calling the method to check any dcc/icc activity exists
        checkDCCOrICCActivityExistsWhenDutyOncallSelected();
    }

    // Prevent mobile keyboard by making date/time inputs readonly and non-interactive
    useEffect(() => {
        const preventKeyboard = () => {
            const inputs = document.querySelectorAll('.activityForm .k-datepicker input, .activityForm .k-timepicker input');
            inputs.forEach((input) => {
                input.setAttribute('readonly', 'readonly');
                input.setAttribute('inputmode', 'none');
                (input as HTMLElement).style.pointerEvents = 'none';
            });
        };
        // Run immediately and after a short delay to catch re-renders
        preventKeyboard();
        setTimeout(preventKeyboard, 100);
    });

    // iOS fix: Handle backspace for recurrence inputs
    useEffect(() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        if (!isIOS || !showRecurrenceEditor) return;
        const attachBackspaceFix = () => {
            const endAfterInput = document.querySelector<HTMLInputElement>('input[type="tel"][id="k-endrule-after"]');
            const intervalInput = document.querySelector<HTMLInputElement>('input[type="tel"][id="interval-editor"]');
            [endAfterInput, intervalInput].forEach(input => {
                if (!input) return;
                if ((input as any)._backspaceListenerAttached) return;
                const handleKeyDown = (e: KeyboardEvent) => {
                    if (e.key === 'Backspace' && input.value.length > 0) {
                        e.preventDefault();
                        const newValue = input.value.slice(0, -1);
                        input.value = newValue;
                        const event = new Event('input', { bubbles: true });
                        input.dispatchEvent(event);
                    }
                };
                input.addEventListener('keydown', handleKeyDown);
                (input as any)._backspaceListenerAttached = true;
                backspaceListeners.current.push({ input, handler: handleKeyDown });
            });
        };
        const timer = setTimeout(attachBackspaceFix, 100);
        return () => {
            clearTimeout(timer);
            backspaceListeners.current.forEach(({ input, handler }) => {
                input.removeEventListener('keydown', handler);
                delete (input as any)._backspaceListenerAttached;
            });
            backspaceListeners.current = [];
        };
    }, [showRecurrenceEditor, props.valueGetter('recurrenceRule')]);

    // Device detection for conditional adaptive mode
    useEffect(() => {
        const checkDevice = () => {
            // Mobile devices: width < 768px (standard tablet/mobile breakpoint)
            setIsMobileDevice(window.innerWidth < 768);
        };
        // Check on mount
        checkDevice();
        // Listen for resize events
        window.addEventListener('resize', checkDevice);
        // Cleanup
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    return (
        <>
            {props.errors.activityConflictsError && <span className="help-block m-b-none text-danger field-validation-valid">{props.errors.activityConflictsError}</span>}
            <FormElement className="activityForm mt-1" horizontal={true}>
                <div className="k-form-field">
                    <Label className="text-start labelAlign">Employer /Practice</Label>
                    <div className="k-form-field-wrap">
                        <Field name={'memberEmployerId'} component={(props) => EmployerEditor(props, employerList)} onChange={(e) => onEmployerChange(e.value)} />
                        {/*{props.errors.memberEmployerId && <span className="help-block m-b-none text-danger field-validation-valid">{props.errors.memberEmployerId}</span>}*/}
                    </div>
                </div>
                <div className="k-form-field">
                    <Label className="labelAlign">Activity</Label>
                    <div className={isDutyOnCallCategorySelected ? "k-form-field-wrap dutyoncallselect" : "k-form-field-wrap"} >
                        <Field name={'jobSubCategoryId'} component={(props) => ActivityEditor(props, filteredActivityList)} onChange={(e) => onActivityChange(e.value)} />
                        {isDutyOnCallCategorySelected && (
                            <div className="mt-1 textcolor">
                                <span >Will show duty/on-call period in your diary. Actual work can be added as separate activities during this period.</span>
                            </div>
                        )}
                        {isDutyOnCallCategorySelectedWhenDCCORICCActivityExists && (
                            <div className="mt-1 textcolor">
                                <span >You have existing activities at the same time as the duty/on-call period. Ensure they are updated to 'Work carried out while on duty/on-call'.</span>
                            </div>
                        )}
                        {props.errors.academicWorkBlockedError && <span className="help-block m-b-none text-danger field-validation-valid">{props.errors.academicWorkBlockedError}</span>}
                    </div>
                </div>
                <div className="k-form-field">
                    <Label className="labelAlign">Start</Label>
                    <div className="k-form-field-wrap">
                        <div className="d-flex gap-2"
                            onClick={(e) => {
                                const target = e.target as HTMLElement;
                                const widget = target.closest('.k-datepicker, .k-timepicker');
                                if (widget) {
                                    if (target.closest('.k-input-button')) return;
                                    const toggleBtn = widget.querySelector('.k-input-button') as HTMLElement;
                                    if (toggleBtn) toggleBtn.click();
                                }
                            }}
                        >
                            <DatePicker
                                value={startDate}
                                onChange={(e) => {
                                    if (e.value) {
                                        handleStartDateChange(e.value);
                                    }
                                }}
                                format="dd/MM/yyyy"
                                adaptive={true}
                            />
                            {isTimePicker && (
                                <TimePicker
                                    key={`start-time-${isMobileDevice ? 'mobile' : 'desktop'}`}
                                    ref={startTimePickerRef}
                                    value={startTime}
                                    onChange={(e) => {
                                        // Get actual selected value from ref.state.candidate (mobile picker selection)
                                        let actualValue = e.value;
                                        if (startTimePickerRef.current) {
                                            const candidateValue = (startTimePickerRef.current as any).state?.candidate;
                                            if (candidateValue) {
                                                actualValue = candidateValue;
                                            }
                                        }
                                        if (actualValue) {
                                            handleStartTimeChange(actualValue);
                                        }
                                    }}
                                    onBlur={() => {
                                        // Auto-apply on blur for desktop only
                                        if (!isMobileDevice && startTimePickerRef.current) {
                                            const ref = startTimePickerRef.current as any;
                                            // Desktop: selected value is in _timeSelector.state.current
                                            const selectedValue = ref._timeSelector?.state?.current;
                                            if (selectedValue && selectedValue.getTime() !== startTime.getTime()) {
                                                handleStartTimeChange(selectedValue);
                                            }
                                        }
                                    }}
                                    format="HH:mm"
                                    steps={{ minute: 1 }}
                                    adaptive={isMobileDevice}
                                />
                            )}
                        </div>
                        {props.errors.start && <span className="help-block m-b-none text-danger field-validation-valid">{props.errors.start}</span>}
                    </div>
                </div>
                <div className="k-form-field">
                    <Label className="labelAlign">End</Label>
                    <div className="k-form-field-wrap">
                        <div className="d-flex gap-2"
                            onClick={(e) => {
                                const target = e.target as HTMLElement;
                                const widget = target.closest('.k-datepicker, .k-timepicker');
                                if (widget) {
                                    if (target.closest('.k-input-button')) return;
                                    const toggleBtn = widget.querySelector('.k-input-button') as HTMLElement;
                                    if (toggleBtn) toggleBtn.click();
                                }
                            }}
                        >
                            <DatePicker
                                value={endDate}
                                onChange={(e) => {
                                    if (e.value) {
                                        handleEndDateChange(e.value);
                                    }
                                }}
                                format="dd/MM/yyyy"
                                adaptive={true}
                            />
                            {isTimePicker && (
                                <TimePicker
                                    ref={endTimePickerRef}
                                    value={endTime}
                                    onChange={(e) => {
                                        // Get actual selected value from ref.state.candidate (mobile picker selection)
                                        let actualValue = e.value;
                                        if (endTimePickerRef.current) {
                                            const candidateValue = (endTimePickerRef.current as any).state?.candidate;
                                            if (candidateValue) {
                                                actualValue = candidateValue;
                                            }
                                        }
                                        if (actualValue) {
                                            handleEndTimeChange(actualValue);
                                        }
                                    }}
                                    onBlur={() => {
                                        // Auto-apply on blur for desktop only
                                        if (!isMobileDevice && endTimePickerRef.current) {
                                            const ref = endTimePickerRef.current as any;
                                            // Desktop: selected value is in _timeSelector.state.current
                                            const selectedValue = ref._timeSelector?.state?.current;
                                            if (selectedValue && selectedValue.getTime() !== endTime.getTime()) {
                                                handleEndTimeChange(selectedValue);
                                            }
                                        }
                                    }}
                                    format="HH:mm"
                                    steps={{ minute: 1 }}
                                    adaptive={isMobileDevice}
                                />
                            )}
                        </div>
                        {props.errors.end && <span className="help-block m-b-none text-danger field-validation-valid">{props.errors.end}</span>}
                    </div>
                </div>

                {/* Quick Select Buttons */}
                {isTimePicker && (
                    <div className="k-form-field">
                        <Label className="k-label text-start labelAlign">Quick select</Label>
                        <div className="k-form-field-wrap d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handleNowClick}>
                                Now
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handleAdd15MinClick}>
                                +15m
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handle1HourBlockClick}>
                                1 Hour Block
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={handle30MinBlockClick}>
                                30 Minute Block
                            </button>
                        </div>
                    </div>
                )}

                <div className="k-form-field">
                    <Label className="labelAlign">Notes</Label>
                    <div className="k-form-field-wrap">
                        <Field name={"notes"} component={TextArea} rows={3} />
                        {props.errors.notes && <span className="help-block m-b-none text-danger field-validation-valid">{props.errors.notes}</span>}
                    </div>
                </div>
                {showRecurrenceEditor && <Field name={"recurrenceRule"} component={props.recurrenceEditor || ''} start={recurrenceStartDate} onChange={(e) => onReccurenceEditorChange(e)} />}
                {isAllDayCheckbox && <div className="k-form-field">
                    <label id="k-is-allday-checkbox" className="text-start labelAlign k-label k-checkbox-label">All Day Event</label>
                    <div className="float-start d-flex flex-row k-form-field-wrap">
                        <Field name={"isAllDay"} component={props.allDayEditor || Checkbox} rows={1} onChange={(e) => onAllDayCheckBoxChange(e.target.value)} />
                    </div>
                </div>}
                {isOnDutyCheckbox && <div className="k-form-field">
                    <label id="k-is-isOnDuty-checkbox" className="labelAlign k-label k-checkbox-label duty-on-call">Work carried out on duty/on-call</label>
                    <div className="float-start d-flex k-form-field-wrap mt-1 mx-1">
                        <Field name={"isOnDuty"} component={props.isOnDuty || Checkbox} rows={1} />
                    </div>
                </div>}
            </FormElement>
        </>
    );
};